import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, ArrowRight, X, Star, BadgeCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin-only panel rendered on TaskDetail. Lets ops allocate, swap, or unassign
 * the agent on a task. A note can be attached and is appended to the chat as a
 * system message visible to client + agent.
 */
export default function AdminAssignAgentCard({ task, onTaskUpdate }) {
  const [agents, setAgents] = useState([]);
  const [pickedAgentId, setPickedAgentId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/agents");
        if (active) setAgents(data);
      } catch (err) {
        if (active) setLoadErr(formatApiError(err));
      }
    })();
    return () => { active = false; };
  }, []);

  const isClosed = ["completed", "cancelled"].includes(task.status);
  const currentAgentLabel = task.agent_name || "unassigned";

  const assign = async () => {
    if (!pickedAgentId) {
      toast.error("Pick an agent first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tasks/${task.task_id}/assign`, {
        agent_id: pickedAgentId,
        note: note || undefined,
      });
      onTaskUpdate?.(data);
      setPickedAgentId("");
      setNote("");
      toast.success(`Task assigned to ${data.agent_name}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    if (!task.agent_id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tasks/${task.task_id}/assign`, {
        agent_id: null,
        note: note || undefined,
      });
      onTaskUpdate?.(data);
      setNote("");
      toast.success("Agent unassigned — task is open again");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (isClosed) return null;

  return (
    <div className="eb-card p-6 md:p-7" data-testid="admin-assign-card">
      <div className="flex items-center gap-2">
        <UserCog className="w-5 h-5 text-[var(--eb-primary)]" />
        <h2 className="text-xl font-[800]">Ops · Allocate agent</h2>
      </div>
      <p className="mt-1 text-sm text-[var(--eb-text-dim)]">
        Current agent: <span className="font-[700] text-[var(--eb-text)]">{currentAgentLabel}</span>
        {task.status === "open" && " — task is on the marketplace"}
      </p>

      <div className="mt-5 grid sm:grid-cols-12 gap-3">
        <div className="sm:col-span-7">
          <label className="eb-label">Pick an agent</label>
          <select
            data-testid="admin-assign-agent-select"
            className="eb-input"
            value={pickedAgentId}
            onChange={(e) => setPickedAgentId(e.target.value)}
            disabled={busy || agents.length === 0}
          >
            <option value="">— Select a verified agent —</option>
            {agents.map((a) => {
              const isCurrent = a.user_id === task.agent_id;
              const rating = a.rating?.toFixed?.(1) || "0.0";
              const label = `${a.name} · ${a.city || "Anywhere"} · ${a.completed_tasks ?? 0} done · ★${rating}${isCurrent ? " (current)" : ""}`;
              return (
                <option key={a.user_id} value={a.user_id} disabled={isCurrent}>
                  {label}
                </option>
              );
            })}
          </select>
          {loadErr && <div className="mt-1 text-xs text-[var(--eb-secondary)]">{loadErr}</div>}
        </div>
        <div className="sm:col-span-5">
          <label className="eb-label">Note (optional)</label>
          <input
            data-testid="admin-assign-note"
            type="text"
            className="eb-input"
            placeholder="Why the change?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          data-testid="admin-assign-submit"
          onClick={assign}
          disabled={busy || !pickedAgentId}
          className="eb-btn eb-btn-primary"
        >
          {task.agent_id ? <BadgeCheck className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
          {task.agent_id ? "Swap agent" : "Allocate agent"}
        </button>
        {task.agent_id && (
          <button
            data-testid="admin-assign-unassign"
            onClick={unassign}
            disabled={busy}
            className="eb-btn eb-btn-ghost"
          >
            <X className="w-4 h-4" /> Unassign
          </button>
        )}
        {agents.length === 0 && !loadErr && (
          <span className="text-xs text-[var(--eb-text-dim)] inline-flex items-center gap-1">
            <Star className="w-3 h-3" /> No verified agents yet
          </span>
        )}
      </div>
    </div>
  );
}
